import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEBOUNCE_VARIANT_DARK } from '@components/debounced-input-search/debounced-input-search.type';
import { EventChannelView } from '@components/modals/event-channel-view';
import { SearchItemModal } from '@components/modals/search-item-modal';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { Company, ROLE_PROMOTER, ROLE_TEAM_LEADER } from '@domain/company';
import { Event, EventListItem } from '@domain/event';
import { useEventNavigation } from '@hooks/use-event-navigation/use-event-navigation';

export function useEventModalChannelView({ company }: { company?: Company }) {
  const { i18n, t } = useTranslation();
  const {
    clubState,
    companyState: { legacyId: companyId },
  } = useContext(CompanyClubContext);
  const { teams, channels, role } = useContext(CompanyAuthContext);

  const { navigateToB2CEvent } = useEventNavigation({
    companyId,
    clubState,
    view: 'all',
    defaultChannelSlug: company?.defaultChannel?.slug,
    language: i18n.language,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventListItem | Event | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [filteredChannelTeams, setfilteredChannelTeams] = useState(teams);
  const [filteredChannels, setFilteredChannels] = useState(channels);

  useEffect(() => {
    if (channels) setFilteredChannels(channels);
    if (teams) setfilteredChannelTeams(teams);
  }, [channels, teams]);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => {
    setSelectedEvent(null);
    setIsOpen(false);
  }, []);

  const onClickToViewEvent = useCallback(
    (event: EventListItem | Event) => {
      setSelectedEvent(event);
      if (role?.name === ROLE_PROMOTER) {
        if (channels?.length === 1) {
          const slug = channels[0].slug;
          navigateToB2CEvent(event, slug);
        } else {
          openModal();
        }
      } else if (role?.name === ROLE_TEAM_LEADER) {
        if (teams?.length === 1 && teams[0].channels.length === 1) {
          const slug = teams[0].channels[0].slug;
          navigateToB2CEvent(event, slug);
        } else {
          openModal();
        }
      } else {
        navigateToB2CEvent(event);
      }
    },
    [role, channels, navigateToB2CEvent, openModal, teams]
  );

  const handleSelectItem = useCallback(() => {
    if (selectedChannelId) {
      if (selectedEvent) {
        navigateToB2CEvent(selectedEvent, selectedChannelId);
      }
      closeModal();
    }
  }, [closeModal, navigateToB2CEvent, selectedEvent, selectedChannelId]);

  const handleSearchChannel = useCallback(
    (value: string) => {
      if (teams?.length) {
        const teamsFiltered = teams
          .map((team) => {
            const teamChannels = team.channels.filter((channel) =>
              channel.name.toLowerCase().includes(value.toLowerCase())
            );
            return { ...team, channels: teamChannels };
          })
          .filter((team) => team.channels.length > 0);
        setfilteredChannelTeams(teamsFiltered);
      } else {
        const channelsFiltered = channels?.filter((channel) =>
          channel.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredChannels(channelsFiltered);
      }
    },
    [teams, channels, setFilteredChannels]
  );

  const renderModal = useCallback(() => {
    return (
      filteredChannelTeams &&
      filteredChannels &&
      selectedEvent && (
        <SearchItemModal
          isOpen={isOpen}
          handleClose={closeModal}
          placeholder={t('offers:admissionTicketSalesChannelsSearchLabel')}
          title={t('offers:selectChannelModalTitle')}
          buttonPrimaryText={t('common:apply')}
          buttonSecondaryText={t('common:discard')}
          handlePrimaryButton={handleSelectItem}
          handleSecondaryButton={closeModal}
          handleSearch={handleSearchChannel}
          showCloseIconButton={true}
          debounceInputVariant={DEBOUNCE_VARIANT_DARK}>
          <EventChannelView
            teams={filteredChannelTeams}
            channels={filteredChannels}
            handleChange={setSelectedChannelId}
            selectedChannelId={selectedChannelId}
          />
        </SearchItemModal>
      )
    );
  }, [
    isOpen,
    closeModal,
    handleSelectItem,
    filteredChannelTeams,
    filteredChannels,
    selectedEvent,
    handleSearchChannel,
    selectedChannelId,
    t,
  ]);

  return {
    isOpen,
    selectedEvent,
    selectedChannelId,
    openModal,
    closeModal,
    onClickToViewEvent,
    navigateToB2CEvent,
    handleSelectItem,
    renderModal,
  };
}
